/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import {
    ACTION_VIEW,
    PageWizardActions,
    PageWizardHeader,
    PageWizardLayout,
    PageWizardTabs,
    useModal,
    MessageBox,
} from '@veeam/components';
import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { questionIconSrc } from '../../icons';
import { WizardDataContext } from '../DialogWizard/contexts/wizard-data-context';
import { PageWizardStore } from './stores';
import { PageWizardWrapper } from './components';

import type { PageWizardProps } from './interfaces';

export const PageWizard = observer(<T,>(props: PageWizardProps<T>) => {
    const [wizardStateStore] = useState(() => new PageWizardStore<T>(props));
    const hideWizardActon = () => {
        props.deactivate();
        props.onClose && props.onClose();
    };

    if (props.externalPageWizardApi) {
        props.externalPageWizardApi.getWizardStore = () => wizardStateStore;
    }

    // mobx force update hack
    // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
    const { currentStepId, reloadVar } = wizardStateStore.baseStore;

    const [dialog, dialogActions] = useModal({
        render: () => (
            <MessageBox
                iconSrc={questionIconSrc}
                header={wizardStateStore.baseStore.header}
                onRequestClose={() => dialogActions.deactivate()}
                actions={[
                    {
                        text: props.closeMessageOkButtonText,
                        onClick() {
                            dialogActions.deactivate();
                            hideWizardActon();
                        },
                    },
                    {
                        text: props.closeMessageCancelButtonText,
                        view: ACTION_VIEW.secondary,
                        onClick() {
                            dialogActions.deactivate();
                        },
                    },
                ]}
            >
                {props.closeMessageText}
            </MessageBox>
        ),
    });

    const readOnlyWizardActions = [
        {
            id: 'cancel',
            text: wizardStateStore.baseStore.currentCancelActionText || 'Close',
            disabled: wizardStateStore.baseStore.loading,
            onClick: () => wizardStateStore.baseStore.finish(hideWizardActon),
        },
    ];

    const wizardActions = props.isReadOnlyWizard ? readOnlyWizardActions :
        [
            {
                id: 'prev',
                text: wizardStateStore.baseStore.currentPrevActionText || 'Previous',
                view: ACTION_VIEW.alternate,
                disabled: wizardStateStore.baseStore.loading ||
                    wizardStateStore.baseStore.isFirstStep,
                onClick: () => wizardStateStore.baseStore.goToPrevStep(),
            },
            {
                id: 'next',
                text: wizardStateStore.baseStore.currentNextActionText || 'Next',
                view: ACTION_VIEW.alternate,
                disabled: wizardStateStore.baseStore.loading ||
                    wizardStateStore.baseStore.isLastStep,
                onClick: () => wizardStateStore.baseStore.goToNextStep(),
            },
            {
                id: 'done',
                text: wizardStateStore.baseStore.currentDoneActionText || 'Finish',
                disabled: wizardStateStore.baseStore.loading ||
                    (!wizardStateStore.baseStore.isEdit && !wizardStateStore.baseStore.isLastStep),
                onClick: () => wizardStateStore.baseStore.finish(hideWizardActon),
            },
            {
                id: 'cancel',
                text: wizardStateStore.baseStore.currentCancelActionText || 'Cancel',
                disabled: wizardStateStore.baseStore.loading || wizardStateStore.baseStore.cancelActionDisabled,
                view: ACTION_VIEW.secondary,
                onClick: () => dialogActions.activate(),
            },
        ];
    const wizardDataProviderStore = useMemo(() => ({ data: wizardStateStore }), [wizardStateStore]);

    return (
        <WizardDataContext.Provider value={wizardDataProviderStore}>
            <PageWizardWrapper>
                <PageWizardLayout
                    header={(
                        <PageWizardHeader
                            hasBack={props.hasBack}
                            onBackClick={() => props.isReadOnlyWizard ? hideWizardActon() : dialogActions.activate()}
                            disabledBack={props.disabledBack === false ? false : wizardStateStore.baseStore.loading}
                            title={props.title}
                            description={props.description}
                            data-uid='page-wizard-header-q-id'
                        />
                    )}
                    tabs={(
                        <PageWizardTabs
                            tabs={wizardStateStore.baseStore.tabsConfig}
                            width={props.tabsWidth}
                        />
                    )}
                    content={(
                        <React.Fragment key={wizardStateStore.baseStore.currentStepId}>
                            {wizardStateStore.baseStore.currentStepElement}
                        </React.Fragment>
                    )}
                    footer={(
                        <PageWizardActions
                            primary={wizardActions}
                        />
                    )}
                    panel={wizardStateStore.isPanelOpened && (
                        <>
                            {wizardStateStore.panelRenderer(() => wizardStateStore.closePanel())}
                        </>
                    )}
                    onKeyPress={(e) => {
                        // Show confirmation dialog on esc key press
                        if (e.keyCode === 27) {
                            e.stopPropagation();
                            dialogActions.activate();
                        }
                    }}
                    pending={wizardStateStore.baseStore.pending}
                    loading={wizardStateStore.baseStore.loading}
                    fullWidthContent={props.fullWidthContent}
                    widthContent={props.widthContent}
                />

                {dialog}
            </PageWizardWrapper>
        </WizardDataContext.Provider>
    );
});
